import { ChevronDown, ChevronRight, Pencil, Save, Trash2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { images } from "../../../../../../assets/images";
import { Button } from "../../../../../../components/ui/common/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "../../../../../../components/ui/common/form";
import { Input } from "../../../../../../components/ui/common/input";
import { PACKAGING_NAME_MAX_LENGTH } from "../packagingLevel.config";

interface PackagingNameSectionProps {
  packagingName: string;
  nameExpanded: boolean;
  onToggleExpanded: () => void;
  onEditName: () => void;
  onDelete: () => void;
  form: UseFormReturn<any>;
  onSaveName: (data: any) => void;
  lockStatus: boolean;
  deleteLockStatus: boolean;
  isNameLocked: boolean;
}

const PackagingNameSection = ({
  packagingName,
  nameExpanded,
  onToggleExpanded,
  onEditName,
  onDelete,
  form,
  onSaveName,
  lockStatus,
  deleteLockStatus,
  isNameLocked
}: PackagingNameSectionProps) => {
  const hasSavedName = !!packagingName?.trim();
  const {
    control,
    handleSubmit,
    formState: { isValid }
  } = form;

  return (
    <div className="tw-border tw-border-border tw-rounded-lg tw-overflow-hidden">
      <button
        type="button"
        onClick={() => hasSavedName && onToggleExpanded()}
        className="tw-flex tw-items-center tw-justify-between tw-w-full tw-px-4 tw-py-3 tw-bg-secondary/40 hover:tw-bg-secondary/60 tw-transition-colors tw-text-left"
      >
        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
          {nameExpanded ? (
            <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
          ) : (
            <ChevronRight className="tw-h-4 tw-w-4 tw-text-muted-foreground tw-shrink-0" />
          )}
          <span className="tw-text-sm tw-font-semibold tw-text-foreground">Packaging Name</span>
          {hasSavedName && !nameExpanded && (
            <span className="tw-text-xs tw-bg-primary/15 tw-text-primary tw-px-2 tw-py-0.5 tw-rounded-full tw-font-medium tw-truncate">
              {packagingName}
            </span>
          )}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          {hasSavedName && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="tw-h-7 tw-gap-1 tw-text-xs"
              onClick={e => {
                e.stopPropagation();
                onEditName();
              }}
            >
              <Pencil className="tw-h-3 tw-w-3" /> Edit
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={deleteLockStatus}
            className="tw-pointer-events-auto tw-gap-1 tw-text-xs tw-h-7 tw-text-destructive hover:tw-bg-destructive/10 hover:tw-text-destructive"
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <Trash2 className="tw-h-3 tw-w-3" /> Delete
          </Button>
        </div>
      </button>

      {nameExpanded && (
        <div className="tw-px-4 tw-py-4 tw-space-y-4">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSaveName)}>
              <FormField
                control={control}
                name="packagingName"
                rules={{
                  required: "Packaging Name is required",
                  validate: (value: string) =>
                    value?.length <= PACKAGING_NAME_MAX_LENGTH ||
                    `Maximum ${PACKAGING_NAME_MAX_LENGTH} characters allowed`
                }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Packaging Name
                      <span className="tw-text-destructive tw-ml-0.5">*</span>
                    </FormLabel>
                    <div className="tw-flex tw-gap-2 tw-items-center">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter packaging name"
                          disabled={isNameLocked || lockStatus}
                          className="tw-h-10 tw-flex-1"
                        />
                      </FormControl>
                      {!isNameLocked && (
                        <Button
                          type="submit"
                          className="tw-h-10 tw-shrink-0 tw-gap-1 tw-px-4"
                          disabled={!isValid || lockStatus}
                        >
                          <Save className="tw-h-3.5 tw-w-3.5" />
                          {hasSavedName ? "Update" : "Save"}
                        </Button>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
          <div className="tw-rounded-md tw-border tw-border-border tw-bg-muted/30 tw-p-3">
            <p className="tw-text-xs tw-text-muted-foreground tw-mb-2 tw-font-medium">
              Packaging Hierarchy (GS1 Reference)
            </p>
            <img
              src={images.PackingProcess}
              alt="Packaging hierarchy: Base Unit → Pack → Case → Pallet"
              className="tw-w-full tw-max-w-2xl tw-mx-auto"
            />
          </div>
        </div>
      )}

      {!hasSavedName && (
        <p className="tw-text-xs tw-text-muted-foreground tw-italic tw-px-4 tw-pb-4">
          Save a packaging name to configure details.
        </p>
      )}
    </div>
  );
};

export default PackagingNameSection;
